import React, { FC, ReactElement, CSSProperties } from 'react';
import Button from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import debounce from 'lodash/debounce';
import { clsx } from 'clsx';
import { stringsRes } from '../../../resources/strings';
import Loader from '../../loader';

export enum ButtonColor {
    DEFAULT = 'inherit',
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
    ERROR = 'error',
}

export enum ButtonType {
    CONFIRM = 'confirm',
    CONFIRM_ICON = 'confirm_icon',
    CANCEL = 'cancel',
    CANCEL_ICON = 'cancel_icon',
    SKIP = 'skip',
    SKIP_ICON = 'skip_icon',
    ERROR = 'error',
    ERROR_ICON = 'error_icon',
    SECONDARY = 'secondary',
    SECONDARY_ICON = 'secondary_icon',
}

export interface ModalButtonProps {
    id?: string;
    type?: ButtonType;
    onClick: () => void;
    icon?: React.ReactNode;
    label?: string;
    disabled?: boolean;
    style?: CSSProperties;
    isLoading?: boolean;
    fullWidth?: boolean;
}

const ModalButton: FC<ModalButtonProps> = (props): ReactElement => {
    const { label, type, icon, onClick, isLoading, fullWidth, ...rest } = props;
    const classes = useStyles();

    const action = debounce(onClick, 300, { leading: true, trailing: false });

    const loaderOrString = (loading: boolean, text: string) => {
        return loading ? <Loader size={20} color="inherit" /> : text;
    };

    const defaultClasses = clsx(classes.modalButton, fullWidth && classes.fullWidth);

    switch (type) {
        case ButtonType.SECONDARY:
            return (
                <Button
                    variant="contained"
                    color={ButtonColor.SECONDARY}
                    className={defaultClasses}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label ?? stringsRes.common.confirm)}
                </Button>
            );
        case ButtonType.SECONDARY_ICON:
            return (
                <Button
                    variant="contained"
                    color={ButtonColor.SECONDARY}
                    className={defaultClasses}
                    startIcon={icon ?? <CheckIcon />}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label ?? stringsRes.common.confirm)}
                </Button>
            );
        case ButtonType.CONFIRM:
            return (
                <Button
                    variant="contained"
                    color={ButtonColor.SECONDARY}
                    className={clsx(defaultClasses, classes.blue)}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label ?? stringsRes.common.confirm)}
                </Button>
            );
        case ButtonType.CONFIRM_ICON:
            return (
                <Button
                    variant="contained"
                    color={ButtonColor.SECONDARY}
                    className={clsx(defaultClasses, classes.blue)}
                    startIcon={<CheckIcon />}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label ?? stringsRes.common.confirm)}
                </Button>
            );
        case ButtonType.CANCEL:
            return (
                <Button
                    variant="contained"
                    color={ButtonColor.DEFAULT}
                    className={clsx(defaultClasses, classes.default)}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label ?? stringsRes.common.cancel)}
                </Button>
            );
        case ButtonType.CANCEL_ICON:
            return (
                <Button
                    variant="contained"
                    color={ButtonColor.DEFAULT}
                    className={clsx(defaultClasses, classes.default)}
                    startIcon={<ArrowBackIcon />}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label ?? stringsRes.common.cancel)}
                </Button>
            );
        case ButtonType.SKIP:
            return (
                <Button
                    variant="contained"
                    color={ButtonColor.DEFAULT}
                    className={clsx(defaultClasses, classes.default)}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label ?? stringsRes.common.skip)}
                </Button>
            );
        case ButtonType.SKIP_ICON:
            return (
                <Button
                    variant="contained"
                    color={ButtonColor.DEFAULT}
                    className={clsx(defaultClasses, classes.default)}
                    startIcon={<SkipNextIcon />}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label ?? stringsRes.common.skip)}
                </Button>
            );
        case ButtonType.ERROR:
            return (
                <Button
                    variant="contained"
                    className={clsx(defaultClasses, classes.danger)}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label ?? stringsRes.common.delete)}
                </Button>
            );
        case ButtonType.ERROR_ICON:
            return (
                <Button
                    variant="contained"
                    className={clsx(defaultClasses, classes.danger)}
                    startIcon={<ErrorIcon />}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label)}
                </Button>
            );
        default:
            return (
                <Button
                    variant="contained"
                    color={ButtonColor.SECONDARY}
                    className={clsx(defaultClasses, classes.default)}
                    onClick={action}
                    id={label}
                    {...rest}
                >
                    {loaderOrString(isLoading, label)}
                </Button>
            );
    }
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        modalButton: {
            fontSize: 16,
            fontWeight: 500, // 900,
            fontFamily: 'Madras',
            borderRadius: 10,
            boxShadow: 'unset',
            maxHeight: 49,
            minWidth: '100px',
            maxWidth: '160px',
            padding: '16px 24px',
            marginLeft: theme.spacing(0.5),
            marginRight: theme.spacing(0.5),
            marginBottom: theme.spacing(0.5),
            '&:hover': {
                boxShadow: 'unset',
            },
            '&.MuiButton-root.Mui-disabled': {
                color: theme.palette.secondary.contrastText,
                backgroundColor: `${theme.palette.secondary.main}4D`,
            },
        },
        fullWidth: {
            minWidth: 'unset',
            maxWidth: 'unset',
        },
        blue: {
            color: theme.palette.secondary.contrastText,
            backgroundColor: theme.palette.secondary.main,
            '&:hover': {
                background: `linear-gradient(0deg, rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0.06)), ${theme.palette.secondary.main}`,
            },
            '&.MuiButton-root.Mui-disabled': {
                color: theme.palette.secondary.contrastText,
                backgroundColor: `${theme.palette.secondary.main}4D`,
            },
        },
        danger: {
            color: theme.palette.colors.fixed.darkWhite,
            backgroundColor: theme.palette.colors.text.error,
            '&:hover': {
                background: `linear-gradient(0deg, rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0.06)), ${theme.palette.colors.text.error}`,
            },
            '&.MuiButton-root.Mui-disabled': {
                color: theme.palette.colors.fixed.darkWhite,
                backgroundColor: `${theme.palette.colors.text.error}4D`,
            },
        },
        default: {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.colors.text.secondaryButton,
            '&:hover': {
                background: `linear-gradient(0deg, rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0.04)), ${theme.palette.colors.text.secondaryButton}`,
            },
            '&.MuiButton-root.Mui-disabled': {
                color: `${theme.palette.primary.main}66`,
                backgroundColor: theme.palette.colors.text.secondaryButton,
            },
        },
    }),
);

export default ModalButton;
